import React from 'react';
import { useInView } from 'react-intersection-observer';

interface HeroProps {
  language: string;
}

const Hero: React.FC<HeroProps> = ({ language }) => {
  const { ref: contentRef, inView: contentVisible } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <header className="relative min-h-screen flex items-center bg-gradient-to-br from-blue-50 to-blue-100">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-[800px] h-[800px] -top-[400px] -right-[400px] rounded-full bg-gradient-to-br from-blue-200 to-transparent opacity-20"></div>
        <div className="absolute w-[600px] h-[600px] -bottom-[300px] -left-[300px] rounded-full bg-gradient-to-tl from-blue-200 to-transparent opacity-20"></div>
        <div className="absolute w-[300px] h-[300px] top-1/3 right-1/4 rounded-full bg-gradient-to-tr from-blue-300 to-transparent opacity-10"></div>
        
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0" style={{ 
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)`,
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="container max-w-6xl mx-auto px-4 py-16 relative z-10 mt-20">
        <div 
          ref={contentRef}
          className={contentVisible ? 'opacity-100' : 'opacity-0'}
        >
          <div className={`max-w-3xl ${contentVisible ? 'animate-slide-up' : ''}`}>  
            <h1 className="text-heading-lg text-blue-900 mb-6 mt-3">
              {language === 'zh'
                ? <><br/>迪衡數位科技結合本地 AI 專業知識與先進技術，提供量身定制的人工智慧轉型解決方案。</>
                : <><br/>DHT Solution blends local AI expertise with advanced tech for tailored AI transformation</>}
            </h1>
            <div className="text-body-lg text-gray-700 mb-8 max-w-2xl space-y-6">
              {language === 'zh' ? (
                <>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-800 mb-2">客製化模型訓練</h2>
                    <ul className="list-disc list-inside ml-4">
                      <li>我們在迪衡數位科技內部使用您的資料對開源模型進行微調，以實現最佳性能。</li>
                    </ul>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-800 mb-2">開發行業特定 AI 代理</h2>
                    <ul className="list-disc list-inside ml-4">
                      <li><b>金融科技：</b>即時市場分析、風險建模</li>
                      <li><b>客戶服務：</b>具品牌語氣的智能聊天機器人</li>
                      <li><b>AI ETL：</b>高效數據提取與洞察</li>
                      <li><b>即時翻譯：</b>高精準度的語言解決方案</li>
                      <li><b>知識管理：</b>智能搜索與推薦</li>
                      <li><b>政府應用：</b>安全、合規的 AI 解決方案</li>
                    </ul>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-800 mb-2">AI 驅動的高性能計算系統</h2>
                    <ul className="list-disc list-inside ml-4">
                      <li>可擴展的 HPC AI 叢集，支援 GPU 共享，優化成本與性能。</li>
                    </ul>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-800 mb-2">優勢</h2>
                    <ul className="list-disc list-inside ml-4">
                      <li>通過自動化簡化運營</li>
                      <li>利用即時分析提升決策</li>
                      <li>通過創新獲得競爭優勢</li>
                      <li>與迪衡數位科技合作，實現高效的 AI 驅動投資回報。</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-800 mb-2">Custom Model Fine-Tuning</h2>
                    <ul className="list-disc list-inside ml-4">
                      <li>We fine-tune open-source models on your data in-house for optimal performance.</li>
                    </ul>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-800 mb-2">Develop Industry-Specific AI Agents</h2>
                    <ul className="list-disc list-inside ml-4">
                      <li><b>Fintech:</b> Real-time market analysis, risk modeling</li>
                      <li><b>Customer Service:</b> Smart chatbots with brand voice</li>
                      <li><b>AI ETL:</b> Efficient data extraction and insights</li>
                      <li><b>Real-Time Translation:</b> High-accuracy language solutions</li>
                      <li><b>Knowledge Management:</b> Smart search and recommendations</li>
                      <li><b>Government:</b> Secure, compliant AI solutions</li>
                    </ul>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-800 mb-2">AI-Powered HPC Systems</h2>
                    <ul className="list-disc list-inside ml-4">
                      <li>Scalable HPC AI clusters with GPU sharing to optimize cost and performance.</li>
                    </ul>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-800 mb-2">Benefits</h2>
                    <ul className="list-disc list-inside ml-4">
                      <li>Streamline operations with automation</li>
                      <li>Enhance decisions with real-time analytics</li>
                      <li>Gain a competitive edge through innovation</li>
                      <li>Partner with DHT Solution for impactful AI-driven ROI.</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Hero;